import React, { Component, Fragment, createRef } from "react";
import PropTypes from "prop-types";
import "./style.scss";
import { Pagination } from "../";
import Tooltip from "../tooltip/index";
import { makeClassName } from "../../../utils";

class Table extends Component {
  constructor(props) {
    super(props);

    // we want to extract our filter/sort functions from our column schema for use later
    // we also need to set some initial state for the filters/sort functions
    const filters = {};
    const sortFunctions = {};
    let activeSortFunction;

    this.props.columns.forEach(col => {
      // extract filters and set initial state
      if (typeof col.filter === "function") {
        filters[col.name] = {
          method: col.filter,
          active: false,
          value: ""
        };
      }

      // extract sort functions and set initial state
      if (typeof col.sort === "function") {
        sortFunctions[col.name] = {
          method: col.sort,
          descending: Boolean(col.descending)
        };
        if (col.defaultSort) {
          activeSortFunction = col.name;
        }
      }
    });

    this.state = {
      currentPage: this.props.page || 1,
      filters,
      sortFunctions,
      activeSortFunction
    };

    this.focusRef = createRef();
  }

  selectSortFunction = name => {
    this.setState({ activeSortFunction: name });
  };

  setSortDirection = (name, descending) => {
    const { activeSortFunction, sortFunctions } = this.state;
    const currentSortFunction = sortFunctions[activeSortFunction];

    sortFunctions[name] = {
      ...currentSortFunction,
      descending
    };

    this.setState({ sortFunctions });
  };

  setFilterState = (name, active) => {
    const filters = this.state.filters;
    const currentFilter = filters[name];
    filters[name] = {
      ...currentFilter,
      value: active ? currentFilter.value : "",
      active
    };

    this.setState({ filters });
  };

  setFilterValue = (name, value) => {
    const filters = this.state.filters;
    const currentFilter = filters[name];

    filters[name] = {
      ...currentFilter,
      value
    };


    this.setState({
      filters
    });
  };

  onPageChange = (page, total) => {
    this.setState({
      currentPage: page
    });
  };

  skipAction = () => {
    if (this.focusRef.current) {
      this.focusRef.current.focus();
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const { itemsPerPage } = this.props;
    const { currentPage } = this.state;

    const newPageCount = Math.ceil(this.filteredData.length / itemsPerPage);
    // if we are left on a non-existant page after filtering then we can
    // move to the last page
    if (Math.max(1, currentPage) > Math.max(1, newPageCount)) {
      this.setState({currentPage: Math.max(1, newPageCount)});
    }
  }

  getFilteredData = data => {
    const { filters } = this.state;

    // iterate over each filter's value: {active, value, filter}
    const filteredData = Object.values(filters).reduce((filtered, filter) => {
      // apply active filters to our data
      if (filter.active && filter.value) {
        return filtered.filter(item => filter.method(item, filter.value));
      }
      // we just pass the data along unfiltered if the filter isn't active
      return filtered;
    }, data);

    return filteredData;
  };

  getSortedData = data => {
    const { activeSortFunction, sortFunctions } = this.state;
    const currentSortFunction = sortFunctions[activeSortFunction];
    if (
      !currentSortFunction ||
      typeof currentSortFunction.method !== "function"
    ) {
      return data;
    }

    const sorted = data.sort(currentSortFunction.method);

    return currentSortFunction.descending ? sorted.reverse() : sorted;
  };

  getPaginatedData = data => {
    const { itemsPerPage } = this.props;
    const { currentPage } = this.state;

    if (typeof itemsPerPage === "undefined") {
      return {
        totalPages: 1,
        tableData: data
      };
    }

    const totalPages = Math.ceil(data.length / itemsPerPage);
    const sliceStart = (currentPage - 1) * itemsPerPage;
    const page = data.slice(sliceStart, sliceStart + itemsPerPage);
    const firstItemNumber = sliceStart + 1;
    const itemCount = data.length;
    const lastItemNumber = Math.min(sliceStart + itemsPerPage, itemCount);

    const statusString = (totalPages > 1) ?
      `Displaying ${data.length ? firstItemNumber : 0} - ${lastItemNumber} out of ${itemCount} results` :
      `Displaying ${data.length ? firstItemNumber : 0} - ${itemCount} out of ${itemCount} results`;

    return {
      totalPages,
      statusString,
      tableData: page
    };
  };

  get filteredData() {
    return this.getFilteredData(this.props.data);
  }

  get sortedData() {
    return this.getSortedData(this.filteredData);
  }

  get paginatedData() {
    return this.getPaginatedData(this.sortedData);
  }

  render() {
    const { id, caption, columns, className, keyFunction } = this.props;
    const {
      tableData,
      totalPages,
      statusString
    } = this.paginatedData;
    const needsPagination = totalPages > 1;
    return (
      <div id={id} className={makeClassName("ucop-table", className)}>
        {needsPagination && (
          <div aria-label="skiplink" role="navigation">
            <button className="skip-link" onClick={this.skipAction}>
              Skip to pagination controls
            </button>
          </div>
        )}
        <section className="results-counter">{statusString}</section>
        <table>
          <caption className="sr_hide">{caption}</caption>
          <tbody>
            {/* COLUMN HEADERS */}
            <tr>
              {/* we iterate over each column and either render a bunch of filter stuff or */}
              {/* just the column's display name */}
              {columns.map((col, idx) =>
                col.filter ? (
                  <th
                    className="filterable-header"
                    key={idx}
                    scope="col"
                    width={col.width}
                  >
                    <span
                      className="header-filter"
                      aria-label={`Filter by: ${col.displayName}`}
                      onClick={() => this.setFilterState(col.name, true)}
                    >
                      {col.displayName}
                      <Tooltip>
                        Click header to {`filter by:${col.displayName}`}
                      </Tooltip>
                    </span>
                    {this.state.filters[col.name].active && (
                      <Fragment>
                        <input
                          className="filter-field"
                          onChange={e =>
                            this.setFilterValue(col.name, e.target.value)
                          }
                          value={this.state.filters[col.name].value}
                        />
                        <button
                          className="btn-close-tooltip"
                          onClick={() => this.setFilterState(col.name, false)}
                        >
                          X
                        </button>
                      </Fragment>
                    )}
                  </th>
                ) : (
                  <th key={idx} scope="col" width={col.width}>
                    {col.displayName}
                  </th>
                )
              )}
            </tr>

            {/* TABLE ROWS */}
            {tableData.length ? (
              tableData.map((item, idx) => (
                <tr key={typeof keyFunction === 'function' ? keyFunction(item, idx) : idx}>
                  {/* we iterate over each column and look up the appropriate data */}
                  {/* if the column has a displayTransformer function we call it with the rest of an item's data */}
                  {/* otherwise we just display the data that matches the column name */}
                  {/* if we don't have anything for that column we render an empty string */}
                  {columns.map((col, idx) => (
                    <td key={idx} width={col.width}>
                      {(typeof col.displayTransformer === "function"
                        ? col.displayTransformer(item)
                        : item[col.name]) || ""}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td className="no-content-table" colSpan={columns.length}>No content in table.</td>
              </tr>
            )}
          </tbody>
        </table>
        {needsPagination && (
          <Pagination
            focusRef={this.focusRef}
            totalPages={totalPages}
            onChange={this.onPageChange}
            page={this.state.currentPage}
          />
        )}
      </div>
    );
  }
}

Table.propTypes = {
  caption: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  itemsPerPage: PropTypes.number,
  id: PropTypes.string,
  page: PropTypes.number
};

export default Table;
